import { useCallback, useMemo, useState } from "react";

import {
  emptyPartySavedRoster,
  mergePlayerNamesMru,
  readPartySavedRoster,
  type PartySavedRosterData,
  writePartySavedRoster,
} from "../utils/partySavedRosterStorage";

export function usePartySavedRoster() {
  const [data, setData] = useState<PartySavedRosterData>(() => readPartySavedRoster());

  const persist = useCallback((updater: (prev: PartySavedRosterData) => PartySavedRosterData) => {
    setData((prev) => {
      const next = updater(prev);
      writePartySavedRoster(next);
      return next;
    });
  }, []);

  const setRememberNames = useCallback(
    (rememberNames: boolean) => {
      persist((prev) => ({ ...prev, rememberNames }));
    },
    [persist],
  );

  const recordPlayerAdded = useCallback(
    (raw: string) => {
      const name = raw.trim();
      if (!name) return;
      persist((prev) => {
        if (!prev.rememberNames) return prev;
        return { ...prev, playerNames: mergePlayerNamesMru(prev.playerNames, [name]) };
      });
    },
    [persist],
  );

  const recordPlayersBatch = useCallback(
    (names: string[]) => {
      const cleaned = names.map((n) => n.trim()).filter(Boolean);
      if (!cleaned.length) return;
      persist((prev) => {
        if (!prev.rememberNames) return prev;
        return { ...prev, playerNames: mergePlayerNamesMru(prev.playerNames, cleaned) };
      });
    },
    [persist],
  );

  const recordTeams = useCallback(
    (teamA: string, teamB: string) => {
      persist((prev) => {
        if (!prev.rememberNames) return prev;
        return { ...prev, teamA: teamA.trim(), teamB: teamB.trim() };
      });
    },
    [persist],
  );

  const clearSaved = useCallback(() => {
    persist(() => emptyPartySavedRoster());
  }, [persist]);

  return useMemo(
    () => ({
      rememberNames: data.rememberNames,
      setRememberNames,
      savedPlayerNames: data.playerNames,
      savedTeamA: data.teamA,
      savedTeamB: data.teamB,
      recordPlayerAdded,
      recordPlayersBatch,
      recordTeams,
      clearSaved,
    }),
    [
      clearSaved,
      data.playerNames,
      data.rememberNames,
      data.teamA,
      data.teamB,
      recordPlayerAdded,
      recordPlayersBatch,
      recordTeams,
      setRememberNames,
    ],
  );
}

export type PartySavedRosterApi = ReturnType<typeof usePartySavedRoster>;
